/**
 * <sk-can-spin> — steps through the can renders in snippets/sk-can-spin.liquid.
 * Drag or swipe the can, use the arrow keys on it, or press the turn buttons.
 * Frames after the first carry data-src until the page has loaded and the
 * viewer is on screen, or until the visitor reaches for it.
 */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const FULL_TURN = 360;
const PEEK_DELAY = 450;
const PEEK_STEP = 115;
const FADE_MS = 160;

const pageLoaded = new Promise((resolve) => {
  if (document.readyState === 'complete') resolve();
  else window.addEventListener('load', () => resolve(), { once: true });
});

function whenIdle(callback) {
  if ('requestIdleCallback' in window) window.requestIdleCallback(callback, { timeout: 2000 });
  else window.setTimeout(callback, 250);
}

function savingData() {
  const connection = navigator.connection;
  if (!connection) return false;
  return Boolean(connection.saveData) || /2g$/.test(connection.effectiveType || '');
}

function isDrawn(frame) {
  return frame.complete && frame.naturalWidth > 0;
}

class CanSpin extends HTMLElement {
  /** @type {HTMLImageElement[]} */
  #frames = [];
  /** @type {HTMLElement | null} */
  #stage = null;
  #views = [];
  #index = 0;
  #degreesPerFrame = 45;
  /** @type {Promise<void> | null} */
  #loading = null;
  #drag = null;
  #handsOn = false;
  #motionTimer = 0;
  #fadeTimer = 0;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    if (this.#abort) return;
    this.#stage = this.querySelector('.sk-can-spin__stage');
    this.#frames = Array.from(this.querySelectorAll('.sk-can-spin__frame'));
    if (!this.#stage || this.#frames.length < 2) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const stage = this.#stage;

    this.#views = (this.dataset.views || '').split('|');
    this.#degreesPerFrame = FULL_TURN / this.#frames.length;
    this.#index = Math.max(0, this.#frames.findIndex((frame) => frame.classList.contains('is-active')));

    stage.setAttribute('role', 'slider');
    stage.setAttribute('tabindex', '0');
    stage.setAttribute('aria-label', stage.dataset.label || 'Can, 360° view');
    stage.setAttribute('aria-valuemin', '0');
    stage.setAttribute('aria-valuemax', String(FULL_TURN - this.#degreesPerFrame));
    this.#announce();

    stage.addEventListener('pointerdown', this.#onPointerDown, { signal });
    stage.addEventListener('pointermove', this.#onPointerMove, { signal });
    stage.addEventListener('pointerup', this.#onPointerEnd, { signal });
    stage.addEventListener('pointercancel', this.#onPointerEnd, { signal });
    stage.addEventListener('lostpointercapture', this.#onPointerEnd, { signal });
    // The product gallery starts its own slide drag on mousedown.
    stage.addEventListener('mousedown', (event) => event.stopPropagation(), { signal });
    stage.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    stage.addEventListener('keydown', this.#onKeyDown, { signal });

    for (const button of this.querySelectorAll('[data-spin-step]')) {
      button.addEventListener(
        'click',
        () => {
          const step = Number(button.getAttribute('data-spin-step')) || 1;
          this.#takeOver();
          this.#load().then(() => this.#turnTo(this.#index + step));
        },
        { signal }
      );
    }

    const warm = () => this.#load();
    this.addEventListener('pointerenter', warm, { once: true, signal });
    this.addEventListener('focusin', warm, { once: true, signal });
    this.addEventListener('touchstart', warm, { once: true, passive: true, signal });

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(this.#onVisible, { threshold: 0.5 });
      this.#observer.observe(this);
    }

    this.classList.add('is-enhanced');
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#observer?.disconnect();
    this.#observer = null;
    this.#stopMotion();
    window.clearTimeout(this.#fadeTimer);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onVisible = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.#observer?.disconnect();
    this.#observer = null;
    if (savingData()) return;
    pageLoaded.then(() => whenIdle(() => this.#load().then(() => this.#peek())));
  };

  /** @returns {Promise<void>} */
  #load() {
    if (!this.#loading) {
      this.#loading = Promise.all(
        this.#frames.map((frame) => {
          if (!frame.getAttribute('src') && frame.dataset.src) {
            if (frame.dataset.srcset) frame.srcset = frame.dataset.srcset;
            frame.src = frame.dataset.src;
          }
          return typeof frame.decode === 'function' ? frame.decode().catch(() => {}) : Promise.resolve();
        })
      ).then(() => {
        this.classList.add('is-ready');
      });
    }
    return this.#loading;
  }

  /** One slow turn back to the front, so people can see the can moves. */
  #peek() {
    if (this.#handsOn || reducedMotion.matches || document.hidden) return;
    let steps = 0;
    const tick = () => {
      if (this.#handsOn) return;
      steps += 1;
      this.#turnTo(this.#index + 1);
      if (steps < this.#frames.length) this.#motionTimer = window.setTimeout(tick, PEEK_STEP);
    };
    this.#motionTimer = window.setTimeout(tick, PEEK_DELAY);
  }

  #takeOver() {
    this.#handsOn = true;
    this.#stopMotion();
  }

  #stopMotion() {
    window.clearTimeout(this.#motionTimer);
    this.#motionTimer = 0;
  }

  /** @param {number} target */
  #turnTo(target) {
    const count = this.#frames.length;
    const next = ((target % count) + count) % count;
    if (next === this.#index) return;

    const incoming = this.#frames[next];
    if (!incoming || !isDrawn(incoming)) {
      this.#load();
      return;
    }

    const outgoing = this.#frames[this.#index];
    const fade = !reducedMotion.matches;

    for (const frame of this.#frames) {
      if (frame !== incoming && frame !== outgoing) frame.classList.remove('is-active', 'is-leaving');
    }
    outgoing?.classList.remove('is-active');
    outgoing?.classList.toggle('is-leaving', fade);
    incoming.classList.remove('is-leaving');
    incoming.classList.add('is-active');

    this.#index = next;
    this.#announce();

    window.clearTimeout(this.#fadeTimer);
    if (fade && outgoing) {
      this.#fadeTimer = window.setTimeout(() => outgoing.classList.remove('is-leaving'), FADE_MS);
    }
  }

  #announce() {
    if (!this.#stage) return;
    const degrees = Math.round(this.#index * this.#degreesPerFrame);
    const view = this.#views[this.#index];
    this.#stage.setAttribute('aria-valuenow', String(degrees));
    this.#stage.setAttribute('aria-valuetext', view ? `${view}, ${degrees}°` : `${degrees}°`);
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (!this.#stage) return;
    this.#takeOver();
    this.#load();

    // One full stage width of drag is one full turn of the can.
    const width = this.#stage.getBoundingClientRect().width || 320;
    const pxPerFrame = Math.min(96, Math.max(24, width / this.#frames.length));

    this.#drag = {
      id: event.pointerId,
      startX: event.clientX,
      startIndex: this.#index,
      pxPerFrame,
      lastX: event.clientX,
      lastTime: event.timeStamp,
      velocity: 0,
    };
    try {
      this.#stage.setPointerCapture(event.pointerId);
    } catch {
      // The pointer can already be gone (released or cancelled) by now.
    }
    this.#stage.classList.add('is-dragging');
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.id) return;

    const frames = Math.trunc((event.clientX - drag.startX) / drag.pxPerFrame);
    this.#turnTo(drag.startIndex + frames);

    const elapsed = event.timeStamp - drag.lastTime;
    if (elapsed > 0) {
      drag.velocity = (event.clientX - drag.lastX) / elapsed;
      drag.lastX = event.clientX;
      drag.lastTime = event.timeStamp;
    }
  };

  /** @param {PointerEvent} event */
  #onPointerEnd = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.id || !this.#stage) return;
    this.#drag = null;
    this.#stage.classList.remove('is-dragging');
    if (this.#stage.hasPointerCapture?.(event.pointerId)) this.#stage.releasePointerCapture(event.pointerId);

    const speed = Math.abs(drag.velocity);
    const flicked = event.type === 'pointerup' && event.timeStamp - drag.lastTime < 90 && speed > 0.45;
    if (flicked && !reducedMotion.matches) {
      this.#coast(Math.sign(drag.velocity), Math.min(this.#frames.length, Math.round(speed * 3)));
    }
  };

  /**
   * @param {number} direction
   * @param {number} frames
   */
  #coast(direction, frames) {
    let remaining = frames;
    let delay = 60;
    const tick = () => {
      if (remaining <= 0) return;
      remaining -= 1;
      this.#turnTo(this.#index + direction);
      delay *= 1.3;
      this.#motionTimer = window.setTimeout(tick, delay);
    };
    this.#motionTimer = window.setTimeout(tick, delay);
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    const steps = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1, PageUp: 2, PageDown: -2 };
    let target;
    if (event.key in steps) target = this.#index + steps[event.key];
    else if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = this.#frames.length - 1;
    else return;

    event.preventDefault();
    event.stopPropagation();
    this.#takeOver();
    this.#load().then(() => this.#turnTo(target));
  };
}

if (!customElements.get('sk-can-spin')) customElements.define('sk-can-spin', CanSpin);
